import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/api';
import { SnackbarContext } from '../App';
import { Briefcase, CheckCircle2, Clock, XCircle, ListTodo, Activity } from 'lucide-react';

export default function Tasks() {
    const showSnackbar = useContext(SnackbarContext);
    const [searchParams] = useSearchParams();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'PENDING');
    const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || 'ALL');

    const fetchTasks = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                status: statusFilter,
                type: typeFilter
            });
            
            const assignedTo = searchParams.get('assignedTo');
            if (assignedTo) params.append('assignedTo', assignedTo);

            const res = await api.get(`/api/tasks?${params.toString()}`);
            setTasks(res.data);
        } catch (err) {
            showSnackbar(err.message || 'Failed to fetch tasks', 'error');
        } finally {
            setLoading(false);
        }
    }, [statusFilter, typeFilter, searchParams, showSnackbar]);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]);

    const handleAction = async (taskId, action) => {
        try {
            const res = await api.post(`/api/tasks/${taskId}/${action}`);
            showSnackbar(res.data.sequenceResumed ? `Task ${action} and Sequence Resumed.` : `Task ${action}`, 'success');
            setTasks(tasks.filter(t => t._id !== taskId));
        } catch (err) {
            showSnackbar(err.message || `Failed to ${action} task`, 'error');
        }
    };

    if (loading) {
        return <div className="p-8 text-muted-foreground flex items-center justify-center animate-pulse"><Activity className="mr-2 h-5 w-5" /> Loading tasks...</div>;
    }

    return (
        <div className="p-8 max-w-6xl mx-auto space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 border-b pb-4 border-primary/20">
                <div>
                    <h1 className="text-3xl font-black tracking-tight text-foreground flex items-center gap-3">
                        <ListTodo className="h-8 w-8 text-primary" /> Operations Desk
                    </h1>
                    <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
                        Manual sequence actions requiring operator intervention. Resolving these items natively unblocks automation logic allowing contact assignments to continue dynamically routing.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <select 
                        value={typeFilter} 
                        onChange={e => setTypeFilter(e.target.value)}
                        className="bg-background border rounded-lg px-3 py-2 text-sm font-bold shadow-sm"
                    >
                        <option value="ALL">All Task Types</option>
                        <option value="TASK_LINKEDIN_MANUAL">LinkedIn Actions</option>
                        <option value="TASK_INTERNAL">Internal Tasks</option>
                    </select>
                    <select 
                        value={statusFilter} 
                        onChange={e => setStatusFilter(e.target.value)}
                        className="bg-background border rounded-lg px-3 py-2 text-sm font-bold shadow-sm"
                    >
                        <option value="PENDING">Pending Action</option>
                        <option value="COMPLETED">Successfully Completed</option>
                        <option value="SKIPPED">Skipped Log</option>
                        <option value="ALL">All Historical</option>
                    </select>
                </div>
            </div>

            {tasks.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground border border-dashed rounded-xl bg-muted/10">
                    <CheckCircle2 className="h-16 w-16 mx-auto mb-4 opacity-50 text-emerald-500" />
                    <h3 className="text-xl font-medium">Clear Queue</h3>
                    <p className="opacity-80 mt-1 max-w-sm mx-auto">No operational boundaries match this explicit filter configuration.</p>
                </div>
            ) : (
                <div className="grid gap-4">
                    {tasks.map(task => {
                        const isLinkedIn = task.type === 'TASK_LINKEDIN_MANUAL';
                        return (
                            <div key={task._id} className="bg-card border rounded-xl shadow-sm p-4 flex items-center gap-6 hover:border-primary/50 transition-colors group">
                                <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${isLinkedIn ? 'bg-blue-100 text-blue-600' : 'bg-emerald-100 text-emerald-600'}`}>
                                    {isLinkedIn ? <Briefcase className="w-6 h-6" /> : <CheckCircle2 className="w-6 h-6" />}
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h3 className="font-bold text-lg text-foreground">{task.title}</h3>
                                        <span className="text-[10px] uppercase font-black tracking-widest bg-muted px-2 py-0.5 rounded-full text-muted-foreground">
                                            {isLinkedIn ? 'LinkedIn' : 'Internal'}
                                        </span>
                                    </div>
                                    <p className="text-sm text-muted-foreground line-clamp-2">
                                        {task.description || 'No detailed instructions provided.'}
                                    </p>
                                    
                                    <div className="mt-3 flex items-center gap-4 text-xs font-medium bg-muted/30 p-2 rounded-md">
                                        <div className="flex items-center gap-1.5 border-r pr-4 truncate max-w-[200px]">
                                            <span className="opacity-50">Contact:</span>
                                            <span className="text-foreground">{task.contactId?.firstName} {task.contactId?.lastName}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 opacity-70 border-r pr-4">
                                            <Clock className="w-3.5 h-3.5" />
                                            <span>Generated {new Date(task.createdAt).toLocaleDateString()}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 font-mono text-primary/80 truncate">
                                            {task.sequenceAssignmentId?.sequenceId?.name} (Node: {task.sequenceNodeId})
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-col flex-wrap gap-2 shrink-0 sm:border-l sm:pl-6 pb-1">
                                    {task.status === 'PENDING' ? (
                                        <>
                                            <button 
                                                onClick={() => handleAction(task._id, 'complete')} 
                                                className="bg-black hover:bg-black/80 dark:bg-white dark:hover:bg-white/90 text-white dark:text-black font-bold uppercase text-[10px] tracking-wider px-6 py-2.5 rounded-lg flex items-center justify-center gap-2 shadow-sm transition-transform active:scale-95"
                                            >
                                                <CheckCircle2 className="w-3.5 h-3.5" /> Mark Completed
                                            </button>
                                            <button 
                                                onClick={() => handleAction(task._id, 'skip')} 
                                                className="bg-transparent hover:bg-red-50 text-red-600 border border-red-200 font-bold uppercase text-[10px] tracking-wider px-6 py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors active:scale-95"
                                            >
                                                <XCircle className="w-3.5 h-3.5" /> Skip Action
                                            </button>
                                        </>
                                    ) : (
                                        <div className="flex flex-col gap-1 items-end pt-2">
                                            <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md ${task.status === 'COMPLETED' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-red-100 text-red-700 border border-red-200'}`}>
                                                {task.status}
                                            </span>
                                            {task.completedAt && (
                                                <span className="text-[10px] text-muted-foreground opacity-70">
                                                    Resolved {new Date(task.completedAt).toLocaleDateString()}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
